import React, { useCallback, useContext, useMemo } from "react";
import {
  BadgeColor,
  EnhancedTable,
  useClientTable,
} from "@pushpress/shared-components";
import { AppContext } from "../../../../context/app";
import { CalendarItemAttendeeFragment } from "./../CalendarDetail-generated.hooks";
import { format } from "date-fns";
import { changeTimeZone } from "../../../../utils/dateUtils";
import AttendanceMenu from "../AttendanceMenu";
import AttendeeAvatarNameColumn from "./AttendeeAvatarNameColumn";
import AttendeeBadgeColumn from "./AttendeeBadgeColumn";
import { ObjectToBeDefined } from "../../../../types/types";

type WaitlsitedTableProps = {
  attendees?: CalendarItemAttendeeFragment[];
  onActionSuccess?: VoidFunction;
};

function getWaitlistTooltip(
  attendee: CalendarItemAttendeeFragment,
  timezone?: string,
) {
  if (
    !attendee.waitlistUntilTimestamp ||
    isNaN(new Date(attendee.waitlistUntilTimestamp).getTime())
  ) {
    return "- -";
  }

  return `Expires ${format(
    changeTimeZone(attendee.waitlistUntilTimestamp * 1000, timezone),
    "MMM d, h:mmaaa",
  )}`;
}

const WaitlistedTable = ({
  attendees = [],
  onActionSuccess,
}: WaitlsitedTableProps) => {
  const app = useContext(AppContext);
  const { client } = app;

  const renderMenu = (
    row: CalendarItemAttendeeFragment,
    onClose: VoidFunction,
  ) => {
    return (
      <AttendanceMenu row={row} onClose={onClose} onSuccess={onActionSuccess} />
    );
  };

  const columns = useMemo(
    () => [
      {
        key: "customerName",
        header: "Name",
        isSortable: true,
        width: "33%",
        sort: (
          a: CalendarItemAttendeeFragment,
          b: CalendarItemAttendeeFragment,
        ) => a?.customerName?.localeCompare(b?.customerName || "") || 0,
        cell: (row: CalendarItemAttendeeFragment) => {
          return <AttendeeAvatarNameColumn attendee={row} />;
        },
      },
      {
        header: "Plan",
        key: "plan",
        isSortable: true,
        width: "33%",
        cell: (row: CalendarItemAttendeeFragment) => {
          return row?.plan ?? "--";
        },
      },
      {
        header: "Status",
        key: "waitlistPriority",
        isSortable: true,
        width: "33%",
        cell: (row: CalendarItemAttendeeFragment) => {
          return (
            <AttendeeBadgeColumn
              label={`Waitlist #${row.waitlistPriority}`}
              color={BadgeColor.Warning}
              tooltip={getWaitlistTooltip(row, client.timezone)}
            />
          );
        },
      },
    ],
    [client.timezone],
  );

  const getRowId = useCallback((row: ObjectToBeDefined) => {
    return row.uuid;
  }, []);

  const {
    data: tableData,
    sort,
    handleSort,
  } = useClientTable({
    data: attendees.map((r) => r),
    columns,
    defaultSort: { key: "waitlistPriority", order: "asc" },
    getRowId: (row) => {
      return row.uuid;
    },
  });

  return (
    <EnhancedTable
      data={tableData}
      columns={columns}
      sort={sort}
      getRowId={getRowId}
      onSort={handleSort}
      renderRowMenu={renderMenu}
    />
  );
};

export default WaitlistedTable;
